import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';
import { apiClient } from '@/lib/api/client';
import { extractTenantSubdomain } from '@/lib/tenant/tenant-host';
import { resolveBrandingValues } from '@/lib/tenant/branding-url';


export type TenantHostStatus =
  | 'PROVISIONING'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'MAINTENANCE'
  | 'DEPROVISIONED';


export interface TenantBranding {
  'branding.logoUrl'?: string;
  'branding.primaryColor'?: string;
  'branding.accentColor'?: string;
  'branding.faviconUrl'?: string;
  'branding.browserTabTitle'?: string;
  'branding.productName'?: string;
  'branding.fontFamily'?: string;
  'branding.heroImages'?: string[];
}

export interface TenantHostInfo {
  subdomain: string;
  name: string;
  code: string;
  status: TenantHostStatus;
  logo: string | null;
  url: string;
  branding?: TenantBranding;
}

const CACHE_KEY_PREFIX = 'minetech.tenant-host.';

function withResolvedBranding(info: TenantHostInfo): TenantHostInfo {
  return info.branding
    ? { ...info, branding: resolveBrandingValues(info.branding) }
    : info;
}

function readCache(hostname: string): TenantHostInfo | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + hostname);
    return raw
      ? withResolvedBranding(JSON.parse(raw) as TenantHostInfo)
      : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(hostname: string, info: TenantHostInfo): void {
  try {
    localStorage.setItem(CACHE_KEY_PREFIX + hostname, JSON.stringify(info));
  } catch {
    // Private browsing / quota exceeded — the network answer still works.
  }
}

export function useTenantByHost() {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const subdomain = extractTenantSubdomain(hostname);

  const query = useQuery<TenantHostInfo>({
    queryKey: ['tenant-by-host', hostname],
    enabled: subdomain !== null,
    queryFn: async () => {
      const { data } = await apiClient.get<TenantHostInfo>(
        '/public/tenant-by-host',
        {
          params: { host: hostname },
          redirectOnError: false,
        },
      );
      writeCache(hostname, data);
      return withResolvedBranding(data);
    },
    placeholderData: subdomain ? readCache(hostname) : undefined,
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    retry: false,
  });

  return {
    ...query,
    subdomain,
    isTenantHost: subdomain !== null,
    isUnconfirmed: query.isPlaceholderData,
  };
}


export function useTenantHostCache(): TenantHostInfo | undefined {
  const client = useQueryClient();
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';

  const subscribe = useCallback(
    (onChange: () => void) => client.getQueryCache().subscribe(onChange),
    [client],
  );
  const snapshot = useCallback(
    () => client.getQueryData<TenantHostInfo>(['tenant-by-host', hostname]),
    [client, hostname],
  );

  return useSyncExternalStore(subscribe, snapshot, () => undefined);
}
